import React from "react";
import { RUNTIME_PATIENT_AUTH_REQUIRED } from "./treatmentApiConstants";
import { TREATMENT_RUNTIME_NOTICE } from "./treatmentUiConstants";
import "./treatmentRuntimeNotice.css";

// Codes that mean "no usable HTTP response": the request never completed.
const NO_RESPONSE_CODES = new Set([
  "treatment_runtime_unavailable",
  "runtime_request_timeout",
  "runtime_resume_failed",
]);

// Outcomes the page decides by itself: repeating the request cannot change them
// (WT-231), so they get start-over only, never Try again.
const SAVED_PROGRESS_CODES = new Set([
  "runtime_pointer_invalid",
  "release_integrity_mismatch",
]);

/** True only for a server failure (5xx) or a network failure (no response). */
const isServiceOutage = (error) => {
  const status = error?.status == null ? null : Number(error.status);
  if (status !== null && Number.isFinite(status) && status >= 500) return true;
  if (status === null || status === 0 || !Number.isFinite(status)) {
    const code = String(error?.code || error?.rawCode || "").toLowerCase();
    return !code || NO_RESPONSE_CODES.has(code);
  }
  return false;
};

/** An HTTP 4xx the server decided: repeating the same request cannot change it (408 can). */
const isClientError = (error) => {
  const status = error?.status == null ? null : Number(error.status);
  return status !== null && Number.isFinite(status) && status >= 400 && status < 500 && status !== 408;
};

const noticeFor = (error) => {
  if (Number(error?.status) === 429) {
    return TREATMENT_RUNTIME_NOTICE.rateLimited;
  }
  const code = String(error?.code || error?.rawCode || "").toLowerCase();
  if (code === RUNTIME_PATIENT_AUTH_REQUIRED) {
    return TREATMENT_RUNTIME_NOTICE.signIn;
  }
  if (SAVED_PROGRESS_CODES.has(code)) {
    return TREATMENT_RUNTIME_NOTICE.savedProgress;
  }
  if (code === "runtime_release_integrity_invalid") {
    return TREATMENT_RUNTIME_NOTICE.integrity;
  }
  const identity = `${error?.code || ""} ${error?.message || ""}`.toLowerCase();
  if (identity.includes("expired") || error?.status === 410) {
    return TREATMENT_RUNTIME_NOTICE.expired;
  }
  if (identity.includes("stale") || error?.status === 409) {
    return TREATMENT_RUNTIME_NOTICE.stale;
  }
  if (identity.includes("snapshot") || identity.includes("capability")) {
    return TREATMENT_RUNTIME_NOTICE.snapshot;
  }
  return isServiceOutage(error)
    ? TREATMENT_RUNTIME_NOTICE.unavailable
    : TREATMENT_RUNTIME_NOTICE.generic;
};

export default function TreatmentRuntimeNotice({
  error,
  busy,
  onRetry,
  onStartOver,
  differentAccount = false,
  children = null,
}) {
  if (!error) return null;
  const baseNotice = noticeFor(error);
  // A retry repeats a request that only a sign-in can change, so it is not offered.
  const requiresSignIn = baseNotice === TREATMENT_RUNTIME_NOTICE.signIn;
  // Signed in once already and refused again: the saved intake belongs to another account.
  const clientError = baseNotice === TREATMENT_RUNTIME_NOTICE.generic && isClientError(error);
  let notice = baseNotice;
  if (requiresSignIn && differentAccount) notice = TREATMENT_RUNTIME_NOTICE.differentAccount;
  else if (clientError) notice = TREATMENT_RUNTIME_NOTICE.genericClientError;
  // An unexpected client error (4xx) cannot change on retry either: Start over only.
  const canRetry = !requiresSignIn && !clientError && notice !== TREATMENT_RUNTIME_NOTICE.savedProgress;
  const retryAfterSeconds = Number(error?.retryAfterSeconds);
  const waitMinutes = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
    ? Math.ceil(retryAfterSeconds / 60)
    : null;
  const isIntegrityError = (
    String(error?.code || error?.rawCode || "").toLowerCase()
    === "runtime_release_integrity_invalid"
  );
  return (
    <section className="treatment-runtime-notice" role="alert">
      <div className="treatment-runtime-notice-icon" aria-hidden="true">!</div>
      <h2>{notice.title}</h2>
      <p>{notice.description}</p>
      {Number(error?.status) === 429 && waitMinutes && (
        <small>Try again in about {waitMinutes} {waitMinutes === 1 ? "minute" : "minutes"}.</small>
      )}
      {error.message && !isIntegrityError && !requiresSignIn && Number(error?.status) !== 429 && <small>{error.message}</small>}
      {children}
      <div className="treatment-runtime-notice-actions">
        {canRetry && (
          <button type="button" disabled={busy} onClick={onRetry}>
            {busy ? "Loading…" : notice.action}
          </button>
        )}
        {Number(error?.status) !== 429 && (clientError || error.action?.code === "start_over") && onStartOver && (
          <button type="button" className="treatment-runtime-notice-btn-secondary" disabled={busy} onClick={onStartOver}>
            {error.action?.label || "Start over"}
          </button>
        )}
      </div>
    </section>
  );
}
